// Interface language: English, हिन्दी (Hindi), தமிழ் (Tamil).
// ---------------------------------------------------------------------------
// Tiny dictionary i18n: `t(key, params)` with {name} interpolation and English
// fallback for any key a language does not define. The choice persists per
// browser and sets <html lang>, which also switches in the Devanagari / Tamil
// web fonts (app.css). Scope: the app's interface and the condition-tracking
// view (status, measures, reasons, treatment, care history). The patient record's
// own free text — visit notes, report impressions, drug names — is shown as
// recorded (English) because it is clinical source data, not interface.
import { writable, derived, get } from 'svelte/store';
import type { ConditionTrack, CareEvent, MeasureTrack } from './tracking/collate.ts';
import { fmtValue } from './tracking/collate.ts';

export type Lang = 'en' | 'hi' | 'ta';
export const LANGS: { id: Lang; label: string; short: string }[] = [
  { id: 'en', label: 'English', short: 'EN' },
  { id: 'hi', label: 'हिन्दी', short: 'हिं' },
  { id: 'ta', label: 'தமிழ்', short: 'த' },
];
const KEY = 'mpwrd.lang';
function initial(): Lang {
  try {
    const q = new URL(location.href).searchParams.get('lang');
    if (q === 'en' || q === 'hi' || q === 'ta') return q;
    const v = localStorage.getItem(KEY);
    if (v === 'en' || v === 'hi' || v === 'ta') return v;
  } catch { /* ignore */ }
  return 'en';
}
export const lang = writable<Lang>(initial());
lang.subscribe((l) => {
  try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  try { document.documentElement.setAttribute('lang', l); } catch { /* ignore */ }
});

type Dict = Record<string, string>;
const en: Dict = {
  // navigation
  'nav.dashboard': 'Dashboard', 'nav.profile': 'Wellness', 'nav.labs': 'Labs', 'nav.imaging': 'Imaging', 'nav.procedures': 'Procedures', 'nav.notes': 'Notes',
  'lang.label': 'Language',
  'demo.badge': 'Demo · sample data', 'demo.badgeTitle': 'You’re viewing sample data. Nothing is saved.',
  // pages
  'page.wellness': 'Wellness', 'page.wellnessSub': '{f} facilities · {t} treatments',
  'page.labs': 'Vitals, Labs, Medications', 'page.imaging': 'Imaging', 'page.procedures': 'Procedures', 'page.notes': 'Notes',
  'labs.vitals': 'Vitals', 'labs.labs': 'Labs', 'labs.meds': 'Medications', 'labs.trends': 'Trends',
  // wellness
  'well.conditions': 'Conditions', 'well.care': 'Care history',
  'well.tracked': 'Conditions tracked', 'well.note': 'read from the reports · mapped to the 3D anatomy', 'well.trackedN': '{n} tracked against labs & vitals',
  'well.attention': 'Needs attention', 'well.edit': 'Edit', 'well.done': 'Done',
  'well.hint': 'Choose a condition to zoom the body to it — tap a marker to open its condition.',
  'well.since': 'since {d}', 'well.layer': '{l} layer', 'well.untracked': 'No labs or vitals are tracked for this condition yet.',
  'well.empty': 'No conditions tracked yet — they appear here as your reports are analyzed.',
  'well.recorded': 'As recorded: {d}',
  // record statuses
  'rec.Active': 'Active', 'rec.Chronic': 'Chronic', 'rec.Resolved': 'Resolved',
  // tracking statuses / trends
  'st.needs-attention': 'Needs attention', 'st.above-target': 'Above target', 'st.improving': 'Improving', 'st.controlled': 'Controlled', 'st.monitoring': 'Monitoring',
  'tr.improving': '↘ improving', 'tr.worsening': '↗ worsening', 'tr.stable': '→ stable', 'tr.insufficient': '',
  // tracking panel
  'trk.target': 'target {t}', 'trk.atGoal12': '{p}% of last 12 mo at goal', 'trk.noRx': 'no treatment on record', 'trk.decides': 'decides status',
  'trk.vsPrev': '{d} vs previous', 'trk.atGoal': 'at goal', 'trk.aboveGoal': 'above goal', 'trk.attention': 'attention {t}',
  'trk.atGoalFor': 'at goal for {n} readings', 'trk.range': 'range {a} ({ad}) – {b} ({bd})',
  'trk.treatment': 'Treatment', 'trk.nothingTreats': 'Nothing on the medication list treats this condition.', 'trk.noMed': 'No medication.', 'trk.wouldCount': 'Would count: {list}.',
  'trk.care': 'Care history', 'trk.showAll': 'Show all {n}', 'trk.showFewer': 'Show fewer',
  // status reasons
  'why.monitoring': 'No numeric measure decides this condition; see screening and care history.',
  'why.attention': '{label} {value} is {thr}.', 'why.noTarget': '{label} {value}; no target defined.',
  'why.controlled': '{label} {value} at goal ({target}) for {n} consecutive readings.', 'why.reached': '{label} {value} just reached goal ({target}).',
  'why.worse': '{label} {value} is above goal ({target}) and worsening.', 'why.improving': '{label} {value} is above goal ({target}) but improving.', 'why.above': '{label} {value} is above goal ({target}).',
  'why.untreated': ' No treatment is on the current medication list.',
  // care history
  'care.start': 'Started', 'care.stop': 'Stopped', 'care.increase': 'Increased', 'care.decrease': 'Reduced', 'care.change': 'Changed',
  'care.med': '{verb} {drug}{dose}', 'care.hypo': 'Hypoglycaemia {g} mg/dL{sev}',
  'care.reached': '{label} reached goal', 'care.left': '{label} left goal', 'care.first': 'First {label} on record', 'care.attention': '{label} needs attention',
  'care.detail': '{value} (target {target}).',
  'scr.retinal': 'Retinal screening', 'scr.foot': 'Foot examination', 'scr.liverUltrasound': 'Liver ultrasound', 'scr.homeBloodPressure': 'Home blood-pressure log',
  // condition names (by tracking spec id)
  'cond.t2dm': 'Type 2 diabetes', 'cond.dyslipidemia': 'Dyslipidaemia', 'cond.hypertension': 'Hypertension', 'cond.dkd': 'Diabetic kidney disease',
  'cond.obesity': 'Obesity', 'cond.steatosis': 'Fatty liver', 'cond.retinopathy': 'Diabetic retinopathy',
  // measures
  'm.hba1c': 'HbA1c', 'm.fastingGlucose': 'Fasting glucose', 'm.eag': 'Estimated average glucose', 'm.ldl': 'LDL cholesterol', 'm.tg': 'Triglycerides',
  'm.hdl': 'HDL cholesterol', 'm.nonHdl': 'Non-HDL cholesterol', 'm.totalCholesterol': 'Total cholesterol', 'm.systolic': 'Systolic BP', 'm.diastolic': 'Diastolic BP',
  'm.heartRate': 'Heart rate', 'm.uacr': 'Urine albumin/creatinine', 'm.egfr': 'eGFR', 'm.creatinine': 'Creatinine', 'm.potassium': 'Potassium',
  'm.bmi': 'BMI', 'm.weight': 'Weight', 'm.waist': 'Waist circumference', 'm.alt': 'ALT', 'm.ast': 'AST',
  // anatomy parts & layers
  'part.eyes': 'eyes', 'part.brain': 'brain', 'part.heart': 'heart', 'part.septum': 'heart septum', 'part.arteries': 'arteries', 'part.lungs': 'lungs', 'part.thyroid': 'thyroid',
  'part.liver': 'liver', 'part.spleen': 'spleen', 'part.pancreas': 'pancreas', 'part.kidney': 'kidney', 'part.bladder': 'bladder', 'part.spine': 'spine',
  'part.psoas': 'psoas', 'part.paraspinal': 'paraspinal', 'part.trunk': 'body composition',
  'layer.skin': 'Skin', 'layer.muscle': 'Muscles', 'layer.vascular': 'Vessels', 'layer.organs': 'Organs', 'layer.skeletal': 'Skeleton', 'layer.nerves': 'Nerves',
  // demo gate
  'gate.title': 'Enter your key',
  'gate.sub': 'This is a demo — no account or password needed. Enter the key you were given: a 6-character tester code opens a sample family record; an access key from the clinic opens a single patient’s full history.',
  'gate.label': 'Your demo key', 'gate.enter': 'Enter demo', 'gate.opening': 'Opening…',
  'gate.note': 'Sample data only — no real patients. Anything you add or edit lives in your browser and resets on reload.',
  'gate.back': '← Back to sign in', 'gate.badCode': 'That code isn’t valid. Check with whoever shared the demo link.',
  'gate.short': 'Enter the 6-character tester code or the access key you were given.',
  // settings · names · header menu
  's.title': "Settings",
  's.account': "Account",
  's.appearance': "Appearance",
  's.access': "Access & History",
  's.data': "Data sources",
  's.agreements': "Agreements",
  's.profile': "Profile",
  's.yourRecord': "Your record",
  's.editProfile': "Edit profile",
  's.logout': "Log out",
  's.country': "Country / region",
  's.change': "Change",
  's.dob': "Date of birth",
  's.age': " · age {n}",
  's.minor': " · minor",
  's.notSet': "Not set",
  's.sex': "Sex",
  's.patientEmail': "Patient email",
  's.restrictions': "Restrictions",
  's.eid': "empowered ID",
  's.coOwners': "Co-owners — equal access granted by the patient",
  's.managedBy': "Managed by",
  's.googleAccount': "Google account",
  's.you': " · you",
  's.managingParent': "managing parent",
  's.guardiansWith': "Parents / guardians with access",
  's.removeGuardian': "Remove guardian",
  's.revoke': "Revoke access",
  's.signin': "Sign-in & security",
  's.google': "Google sign-in",
  's.googleSub': "You sign in securely with Google — there’s no separate password to manage.",
  's.twofa': "Two-factor authentication (2FA)",
  's.authApp': "Authenticator app",
  's.soon': "Soon",
  's.activity': "Activity & integrity",
  's.signedKey': "Signed in with your key:",
  's.verified': "✓ verified",
  's.tampered': "⚠ tampered",
  's.noActivity': "No activity yet.",
  's.typeface': "Typeface",
  's.textSize': "Text size",
  's.preview': "Preview",
  's.theme': "Theme",
  's.dark': "Dark",
  's.light': "Light",
  's.system': "System",
  's.language': "Language",
  's.scale.0': "Default",
  's.scale.1': "Large",
  's.scale.2': "X-Large",
  's.scale.3': "XX-Large",
  's.careTeam': "Care team — access",
  's.guardiansFor': "Guardians who can view & add to {name}’s record:",
  's.addGuardian': "Add guardian",
  's.guardianNote': "They sign in with this Google email and the profile appears under their account.",
  's.sharedWithYou': "This profile is shared with you by its owner.",
  's.providers': "Service providers — access",
  's.providersSub': "Grant a practitioner (doctor) access to one child’s record with their Google account. Sharing is per-profile — a provider sees only the child you select, not your other children.",
  's.sharingFor': "Sharing record for",
  's.unnamed': "Unnamed",
  's.thisPatient': "this patient",
  's.noProviders': "No service providers on {name}’s record yet.",
  's.addTo': "Add to {name}",
  's.providerNote': "The provider signs in with this Google email and only {name}’s record appears under their account.",
  's.accessTitle': "Account access — last 30 days",
  's.whoOpened': "Who has opened {name}’s record, newest first.",
  's.viewsPeople': " {v} views · {p} people.",
  's.loading': "Loading…",
  's.noAccess': "No access recorded in the last 30 days.",
  's.youCap': "You",
  's.someone': "Someone",
  's.youTag': "you",
  's.sharedAccess': "shared access",
  's.viewsLast': "{n} views · last {when}",
  's.accessNote': "Repeat visits are grouped per person. Access is recorded once per sign-in session; entries older than 30 days aren’t shown.",
  's.accountAccess': "Account access",
  's.ownerOnly': "Access history is available to the record owner.",
  's.dataSub': "Add data conversationally with the empowered assistant, or upload records (C-CDA, PDF, DICOM, CSV, Excel) — each saved as an immutable record in your encrypted account.",
  's.uploads': "Uploads & imports",
  's.connected': "Connected sources",
  's.epicSub': "Connect your hospital’s Epic account to sync records automatically.",
  's.agrSub': "Sign-on agreements you have accepted — open one to read the full text and your signature:",
  's.version': "Version {v}",
  's.accepted': "Accepted {d}",
  's.viewSig': "View & signature",
  's.noAgr': "No agreement acceptance on record yet.",
  's.pvTitle': "Text size preview",
  's.pvBody': "Temperature 98.6 °F · Heart rate 88 bpm · WBC 9.6 kU/μL. This is how body text reads across the app at the selected size.",
  's.pvLabel': "Label",
  's.close': "Close",
  's.signed': "Digitally signed",
  's.effective': "Effective {d}",
  's.legalNote': "The agreement is shown in English, the language in which it was signed.",
  'rel.self': "Self",
  'rel.spouse': "Spouse",
  'rel.parent': "Parent",
  'rel.child': "Child",
  'rel.dependent': "Dependent",
  'rel.other': "Other",
  'sex.Male': "Male",
  'sex.Female': "Female",
  'hdr.profilesUnder': "Profiles under your account",
  'hdr.shared': " · shared",
  'hdr.addProfile': "+ Add profile",
  'hdr.default': "Set as default on login",
  'hdr.defaultOn': "Default on login (click to unset)",
  'hdr.assistant': "empowered assistant",
  'hdr.account': "Account & profiles",
  'hdr.yourAccount': "Your account",
};

const hi: Dict = {
  'nav.dashboard': 'डैशबोर्ड', 'nav.profile': 'स्वास्थ्य', 'nav.labs': 'जाँचें', 'nav.imaging': 'इमेजिंग', 'nav.procedures': 'प्रक्रियाएँ', 'nav.notes': 'नोट्स',
  'lang.label': 'भाषा',
  'demo.badge': 'डेमो · नमूना डेटा', 'demo.badgeTitle': 'आप नमूना डेटा देख रहे हैं। कुछ भी सहेजा नहीं जाता।',
  'page.wellness': 'स्वास्थ्य', 'page.wellnessSub': '{f} सुविधाएँ · {t} उपचार',
  'page.labs': 'वाइटल्स, जाँचें, दवाएँ', 'page.imaging': 'इमेजिंग', 'page.procedures': 'प्रक्रियाएँ', 'page.notes': 'नोट्स',
  'labs.vitals': 'वाइटल्स', 'labs.labs': 'जाँचें', 'labs.meds': 'दवाएँ', 'labs.trends': 'रुझान',
  'well.conditions': 'स्थितियाँ', 'well.care': 'देखभाल इतिहास',
  'well.tracked': 'ट्रैक की जा रही स्थितियाँ', 'well.note': 'रिपोर्टों से लिया गया · 3D शरीर-रचना पर दर्शाया गया', 'well.trackedN': '{n} जाँच और वाइटल्स से जुड़ी',
  'well.attention': 'ध्यान देने की ज़रूरत', 'well.edit': 'संपादित करें', 'well.done': 'हो गया',
  'well.hint': 'शरीर पर ज़ूम करने के लिए कोई स्थिति चुनें — उसकी स्थिति खोलने के लिए किसी मार्कर पर टैप करें।',
  'well.since': '{d} से', 'well.layer': '{l} परत', 'well.untracked': 'इस स्थिति के लिए अभी कोई जाँच या वाइटल्स ट्रैक नहीं किए जा रहे।',
  'well.empty': 'अभी कोई स्थिति ट्रैक नहीं की जा रही — रिपोर्टों के विश्लेषण के साथ वे यहाँ दिखेंगी।',
  'well.recorded': 'रिकॉर्ड के अनुसार: {d}',
  'rec.Active': 'सक्रिय', 'rec.Chronic': 'दीर्घकालिक', 'rec.Resolved': 'ठीक हो चुका',
  'st.needs-attention': 'ध्यान देने की ज़रूरत', 'st.above-target': 'लक्ष्य से ऊपर', 'st.improving': 'सुधार हो रहा है', 'st.controlled': 'नियंत्रित', 'st.monitoring': 'निगरानी में',
  'tr.improving': '↘ सुधार', 'tr.worsening': '↗ बिगड़ रहा', 'tr.stable': '→ स्थिर',
  'trk.target': 'लक्ष्य {t}', 'trk.atGoal12': 'पिछले 12 महीनों में {p}% लक्ष्य पर', 'trk.noRx': 'कोई उपचार दर्ज नहीं', 'trk.decides': 'स्थिति तय करता है',
  'trk.vsPrev': 'पिछली बार से {d}', 'trk.atGoal': 'लक्ष्य पर', 'trk.aboveGoal': 'लक्ष्य से ऊपर', 'trk.attention': 'ध्यान सीमा {t}',
  'trk.atGoalFor': 'लगातार {n} रीडिंग से लक्ष्य पर', 'trk.range': 'सीमा {a} ({ad}) – {b} ({bd})',
  'trk.treatment': 'उपचार', 'trk.nothingTreats': 'दवा सूची में इस स्थिति का कोई उपचार नहीं है।', 'trk.noMed': 'कोई दवा नहीं।', 'trk.wouldCount': 'इन्हें उपचार माना जाएगा: {list}।',
  'trk.care': 'देखभाल इतिहास', 'trk.showAll': 'सभी {n} दिखाएँ', 'trk.showFewer': 'कम दिखाएँ',
  'why.monitoring': 'इस स्थिति को कोई संख्यात्मक माप तय नहीं करता; जाँच और देखभाल इतिहास देखें।',
  'why.attention': '{label} {value} — ध्यान सीमा ({thr}) पार।', 'why.noTarget': '{label} {value}; कोई लक्ष्य तय नहीं।',
  'why.controlled': '{label} {value} लगातार {n} रीडिंग से लक्ष्य ({target}) पर है।', 'why.reached': '{label} {value} अभी लक्ष्य ({target}) पर पहुँचा है।',
  'why.worse': '{label} {value} लक्ष्य ({target}) से ऊपर है और बिगड़ रहा है।', 'why.improving': '{label} {value} लक्ष्य ({target}) से ऊपर है, पर सुधार हो रहा है।', 'why.above': '{label} {value} लक्ष्य ({target}) से ऊपर है।',
  'why.untreated': ' वर्तमान दवा सूची में कोई उपचार नहीं है।',
  'care.start': 'शुरू किया', 'care.stop': 'बंद किया', 'care.increase': 'बढ़ाया', 'care.decrease': 'घटाया', 'care.change': 'बदला',
  'care.med': '{drug}{dose} — {verb}', 'care.hypo': 'हाइपोग्लाइसीमिया (कम शुगर) {g} mg/dL{sev}',
  'care.reached': '{label} लक्ष्य पर पहुँचा', 'care.left': '{label} लक्ष्य से बाहर गया', 'care.first': 'पहली दर्ज {label}', 'care.attention': '{label} पर ध्यान देने की ज़रूरत',
  'care.detail': '{value} (लक्ष्य {target})।',
  'scr.retinal': 'रेटिना जाँच', 'scr.foot': 'पैर की जाँच', 'scr.liverUltrasound': 'लिवर अल्ट्रासाउंड', 'scr.homeBloodPressure': 'घर पर रक्तचाप रिकॉर्ड',
  'cond.t2dm': 'टाइप 2 मधुमेह', 'cond.dyslipidemia': 'डिस्लिपिडेमिया (कोलेस्ट्रॉल विकार)', 'cond.hypertension': 'उच्च रक्तचाप', 'cond.dkd': 'मधुमेह संबंधी गुर्दा रोग',
  'cond.obesity': 'मोटापा', 'cond.steatosis': 'फैटी लिवर', 'cond.retinopathy': 'मधुमेह रेटिनोपैथी',
  'm.fastingGlucose': 'फास्टिंग ग्लूकोज़', 'm.eag': 'अनुमानित औसत ग्लूकोज़', 'm.ldl': 'LDL कोलेस्ट्रॉल', 'm.tg': 'ट्राइग्लिसराइड्स',
  'm.hdl': 'HDL कोलेस्ट्रॉल', 'm.nonHdl': 'नॉन-HDL कोलेस्ट्रॉल', 'm.totalCholesterol': 'कुल कोलेस्ट्रॉल', 'm.systolic': 'सिस्टोलिक रक्तचाप', 'm.diastolic': 'डायस्टोलिक रक्तचाप',
  'm.heartRate': 'हृदय गति', 'm.uacr': 'मूत्र एल्ब्यूमिन/क्रिएटिनिन', 'm.creatinine': 'क्रिएटिनिन', 'm.potassium': 'पोटैशियम',
  'm.weight': 'वज़न', 'm.waist': 'कमर की माप',
  'part.eyes': 'आँखें', 'part.brain': 'मस्तिष्क', 'part.heart': 'हृदय', 'part.septum': 'हृदय पट', 'part.arteries': 'धमनियाँ', 'part.lungs': 'फेफड़े', 'part.thyroid': 'थायरॉइड',
  'part.liver': 'यकृत', 'part.spleen': 'प्लीहा', 'part.pancreas': 'अग्न्याशय', 'part.kidney': 'गुर्दा', 'part.bladder': 'मूत्राशय', 'part.spine': 'रीढ़',
  'part.psoas': 'सोआस मांसपेशी', 'part.paraspinal': 'रीढ़ के पास की मांसपेशियाँ', 'part.trunk': 'शरीर संरचना',
  'layer.skin': 'त्वचा', 'layer.muscle': 'मांसपेशियाँ', 'layer.vascular': 'रक्त वाहिकाएँ', 'layer.organs': 'अंग', 'layer.skeletal': 'कंकाल', 'layer.nerves': 'तंत्रिकाएँ',
  'gate.title': 'अपनी कुंजी दर्ज करें',
  'gate.sub': 'यह एक डेमो है — किसी खाते या पासवर्ड की ज़रूरत नहीं। आपको दी गई कुंजी दर्ज करें: 6 अक्षरों का टेस्टर कोड एक नमूना पारिवारिक रिकॉर्ड खोलता है; क्लिनिक की एक्सेस कुंजी एक रोगी का पूरा इतिहास खोलती है।',
  'gate.label': 'आपकी डेमो कुंजी', 'gate.enter': 'डेमो खोलें', 'gate.opening': 'खुल रहा है…',
  'gate.note': 'केवल नमूना डेटा — कोई वास्तविक रोगी नहीं। आप जो भी जोड़ें या बदलें वह आपके ब्राउज़र में रहता है और रीलोड करने पर रीसेट हो जाता है।',
  'gate.back': '← साइन इन पर वापस', 'gate.badCode': 'यह कोड मान्य नहीं है। डेमो लिंक साझा करने वाले से पुष्टि करें।',
  'gate.short': '6 अक्षरों का टेस्टर कोड या आपको दी गई एक्सेस कुंजी दर्ज करें।',
  // settings · names · header menu
  's.title': "सेटिंग्स",
  's.account': "खाता",
  's.appearance': "रूप-रंग",
  's.access': "पहुँच और इतिहास",
  's.data': "डेटा स्रोत",
  's.agreements': "समझौते",
  's.profile': "प्रोफ़ाइल",
  's.yourRecord': "आपका रिकॉर्ड",
  's.editProfile': "प्रोफ़ाइल संपादित करें",
  's.logout': "लॉग आउट",
  's.country': "देश / क्षेत्र",
  's.change': "बदलें",
  's.dob': "जन्म तिथि",
  's.age': " · आयु {n}",
  's.minor': " · नाबालिग",
  's.notSet': "सेट नहीं",
  's.sex': "लिंग",
  's.patientEmail': "रोगी ईमेल",
  's.restrictions': "प्रतिबंध",
  's.eid': "empowered ID",
  's.coOwners': "सह-स्वामी — रोगी द्वारा दी गई समान पहुँच",
  's.managedBy': "प्रबंधक",
  's.googleAccount': "Google खाता",
  's.you': " · आप",
  's.managingParent': "प्रबंधक अभिभावक",
  's.guardiansWith': "पहुँच वाले माता-पिता / अभिभावक",
  's.removeGuardian': "अभिभावक हटाएँ",
  's.revoke': "पहुँच हटाएँ",
  's.signin': "साइन-इन और सुरक्षा",
  's.google': "Google साइन-इन",
  's.googleSub': "आप Google से सुरक्षित रूप से साइन इन करते हैं — अलग पासवर्ड की ज़रूरत नहीं।",
  's.twofa': "दो-चरणीय सत्यापन (2FA)",
  's.authApp': "ऑथेंटिकेटर ऐप",
  's.soon': "जल्द",
  's.activity': "गतिविधि और अखंडता",
  's.signedKey': "आपकी कुंजी से साइन इन:",
  's.verified': "✓ सत्यापित",
  's.tampered': "⚠ छेड़छाड़",
  's.noActivity': "अभी कोई गतिविधि नहीं।",
  's.typeface': "टाइपफ़ेस",
  's.textSize': "अक्षर आकार",
  's.preview': "पूर्वावलोकन",
  's.theme': "थीम",
  's.dark': "गहरा",
  's.light': "हल्का",
  's.system': "सिस्टम",
  's.language': "भाषा",
  's.scale.0': "सामान्य",
  's.scale.1': "बड़ा",
  's.scale.2': "अधिक बड़ा",
  's.scale.3': "सबसे बड़ा",
  's.careTeam': "देखभाल टीम — पहुँच",
  's.guardiansFor': "{name} के रिकॉर्ड को देख और उसमें जोड़ सकने वाले अभिभावक:",
  's.addGuardian': "अभिभावक जोड़ें",
  's.guardianNote': "वे इस Google ईमेल से साइन इन करते हैं और प्रोफ़ाइल उनके खाते में दिखती है।",
  's.sharedWithYou': "यह प्रोफ़ाइल इसके स्वामी ने आपके साथ साझा की है।",
  's.providers': "सेवा प्रदाता — पहुँच",
  's.providersSub': "किसी चिकित्सक (डॉक्टर) को उनके Google खाते से एक बच्चे के रिकॉर्ड की पहुँच दें। साझाकरण प्रति-प्रोफ़ाइल है — प्रदाता केवल आपके चुने हुए बच्चे को देखता है, आपके अन्य बच्चों को नहीं।",
  's.sharingFor': "किसका रिकॉर्ड साझा कर रहे हैं",
  's.unnamed': "बेनाम",
  's.thisPatient': "इस रोगी",
  's.noProviders': "{name} के रिकॉर्ड पर अभी कोई सेवा प्रदाता नहीं।",
  's.addTo': "{name} में जोड़ें",
  's.providerNote': "प्रदाता इस Google ईमेल से साइन इन करता है और उसके खाते में केवल {name} का रिकॉर्ड दिखता है।",
  's.accessTitle': "खाता पहुँच — पिछले 30 दिन",
  's.whoOpened': "{name} का रिकॉर्ड किसने खोला, नवीनतम पहले।",
  's.viewsPeople': " {v} बार देखा · {p} व्यक्ति।",
  's.loading': "लोड हो रहा है…",
  's.noAccess': "पिछले 30 दिनों में कोई पहुँच दर्ज नहीं।",
  's.youCap': "आप",
  's.someone': "कोई",
  's.youTag': "आप",
  's.sharedAccess': "साझा पहुँच",
  's.viewsLast': "{n} बार · अंतिम {when}",
  's.accessNote': "बार-बार आने को प्रति व्यक्ति समूहित किया जाता है। पहुँच हर साइन-इन सत्र में एक बार दर्ज होती है; 30 दिनों से पुरानी प्रविष्टियाँ नहीं दिखाई जातीं।",
  's.accountAccess': "खाता पहुँच",
  's.ownerOnly': "पहुँच इतिहास केवल रिकॉर्ड स्वामी के लिए उपलब्ध है।",
  's.dataSub': "empowered सहायक से बातचीत करके डेटा जोड़ें, या रिकॉर्ड अपलोड करें (C-CDA, PDF, DICOM, CSV, Excel) — हर एक आपके एन्क्रिप्टेड खाते में अपरिवर्तनीय रिकॉर्ड के रूप में सहेजा जाता है।",
  's.uploads': "अपलोड और आयात",
  's.connected': "जुड़े स्रोत",
  's.epicSub': "रिकॉर्ड अपने-आप सिंक करने के लिए अपने अस्पताल का Epic खाता जोड़ें।",
  's.agrSub': "आपके द्वारा स्वीकार किए गए साइन-ऑन समझौते — पूरा पाठ और अपने हस्ताक्षर देखने के लिए एक खोलें:",
  's.version': "संस्करण {v}",
  's.accepted': "स्वीकृत {d}",
  's.viewSig': "देखें और हस्ताक्षर",
  's.noAgr': "अभी कोई समझौता स्वीकृति दर्ज नहीं।",
  's.pvTitle': "अक्षर आकार पूर्वावलोकन",
  's.pvBody': "तापमान 98.6 °F · हृदय गति 88 bpm · WBC 9.6 kU/μL. चुने गए आकार पर ऐप में मुख्य पाठ ऐसा दिखेगा।",
  's.pvLabel': "लेबल",
  's.close': "बंद करें",
  's.signed': "डिजिटल रूप से हस्ताक्षरित",
  's.effective': "प्रभावी {d}",
  's.legalNote': "समझौता अंग्रेज़ी में दिखाया गया है, जिस भाषा में इस पर हस्ताक्षर किए गए थे।",
  'rel.self': "स्वयं",
  'rel.spouse': "जीवनसाथी",
  'rel.parent': "माता-पिता",
  'rel.child': "संतान",
  'rel.dependent': "आश्रित",
  'rel.other': "अन्य",
  'sex.Male': "पुरुष",
  'sex.Female': "महिला",
  'hdr.profilesUnder': "आपके खाते की प्रोफ़ाइलें",
  'hdr.shared': " · साझा",
  'hdr.addProfile': "+ प्रोफ़ाइल जोड़ें",
  'hdr.default': "लॉगिन पर डिफ़ॉल्ट बनाएँ",
  'hdr.defaultOn': "लॉगिन पर डिफ़ॉल्ट (हटाने के लिए क्लिक करें)",
  'hdr.assistant': "empowered सहायक",
  'hdr.account': "खाता और प्रोफ़ाइलें",
  'hdr.yourAccount': "आपका खाता",
};

const ta: Dict = {
  'nav.dashboard': 'டாஷ்போர்டு', 'nav.profile': 'நலவாழ்வு', 'nav.labs': 'பரிசோதனைகள்', 'nav.imaging': 'ஸ்கேன்கள்', 'nav.procedures': 'சிகிச்சை முறைகள்', 'nav.notes': 'குறிப்புகள்',
  'lang.label': 'மொழி',
  'demo.badge': 'டெமோ · மாதிரித் தரவு', 'demo.badgeTitle': 'நீங்கள் மாதிரித் தரவைப் பார்க்கிறீர்கள். எதுவும் சேமிக்கப்படாது.',
  'page.wellness': 'நலவாழ்வு', 'page.wellnessSub': '{f} மருத்துவ மையங்கள் · {t} சிகிச்சைகள்',
  'page.labs': 'உயிர்க்குறிகள், பரிசோதனைகள், மருந்துகள்', 'page.imaging': 'ஸ்கேன்கள்', 'page.procedures': 'சிகிச்சை முறைகள்', 'page.notes': 'குறிப்புகள்',
  'labs.vitals': 'உயிர்க்குறிகள்', 'labs.labs': 'பரிசோதனைகள்', 'labs.meds': 'மருந்துகள்', 'labs.trends': 'போக்குகள்',
  'well.conditions': 'நோய் நிலைகள்', 'well.care': 'சிகிச்சை வரலாறு',
  'well.tracked': 'கண்காணிக்கப்படும் நோய் நிலைகள்', 'well.note': 'அறிக்கைகளிலிருந்து எடுக்கப்பட்டது · 3D உடற்கூறில் குறிக்கப்பட்டது', 'well.trackedN': '{n} பரிசோதனை மற்றும் உயிர்க்குறிகளுடன் கண்காணிக்கப்படுகின்றன',
  'well.attention': 'கவனம் தேவை', 'well.edit': 'திருத்து', 'well.done': 'முடிந்தது',
  'well.hint': 'உடலில் பெரிதாக்கிக் காண ஒரு நோய் நிலையைத் தேர்ந்தெடுக்கவும் — அந்த நிலையைத் திறக்க ஒரு குறியைத் தட்டவும்.',
  'well.since': '{d} முதல்', 'well.layer': '{l} அடுக்கு', 'well.untracked': 'இந்த நிலைக்கு இன்னும் பரிசோதனைகளோ உயிர்க்குறிகளோ கண்காணிக்கப்படவில்லை.',
  'well.empty': 'இன்னும் எந்த நோய் நிலையும் கண்காணிக்கப்படவில்லை — அறிக்கைகள் பகுப்பாய்வு செய்யப்படும்போது இங்கே தோன்றும்.',
  'well.recorded': 'பதிவில் உள்ளபடி: {d}',
  'rec.Active': 'செயலில்', 'rec.Chronic': 'நீண்டகால', 'rec.Resolved': 'குணமடைந்தது',
  'st.needs-attention': 'கவனம் தேவை', 'st.above-target': 'இலக்கை விட அதிகம்', 'st.improving': 'முன்னேற்றம்', 'st.controlled': 'கட்டுப்பாட்டில்', 'st.monitoring': 'கண்காணிப்பில்',
  'tr.improving': '↘ முன்னேற்றம்', 'tr.worsening': '↗ மோசமடைகிறது', 'tr.stable': '→ நிலையானது',
  'trk.target': 'இலக்கு {t}', 'trk.atGoal12': 'கடந்த 12 மாதங்களில் {p}% இலக்கில்', 'trk.noRx': 'சிகிச்சை பதிவு இல்லை', 'trk.decides': 'நிலையைத் தீர்மானிக்கிறது',
  'trk.vsPrev': 'முந்தையதை விட {d}', 'trk.atGoal': 'இலக்கில்', 'trk.aboveGoal': 'இலக்கை விட அதிகம்', 'trk.attention': 'கவன வரம்பு {t}',
  'trk.atGoalFor': 'தொடர்ந்து {n} அளவீடுகளாக இலக்கில்', 'trk.range': 'வரம்பு {a} ({ad}) – {b} ({bd})',
  'trk.treatment': 'சிகிச்சை', 'trk.nothingTreats': 'மருந்துப் பட்டியலில் இந்த நிலைக்கு எந்த சிகிச்சையும் இல்லை.', 'trk.noMed': 'மருந்து இல்லை.', 'trk.wouldCount': 'சிகிச்சையாகக் கருதப்படுபவை: {list}.',
  'trk.care': 'சிகிச்சை வரலாறு', 'trk.showAll': 'அனைத்து {n} காட்டு', 'trk.showFewer': 'குறைவாகக் காட்டு',
  'why.monitoring': 'இந்த நிலையைத் தீர்மானிக்க எண் அளவீடு இல்லை; பரிசோதனை மற்றும் சிகிச்சை வரலாற்றைப் பார்க்கவும்.',
  'why.attention': '{label} {value} — கவன வரம்பை ({thr}) தாண்டியது.', 'why.noTarget': '{label} {value}; இலக்கு வரையறுக்கப்படவில்லை.',
  'why.controlled': '{label} {value} தொடர்ந்து {n} அளவீடுகளாக இலக்கில் ({target}) உள்ளது.', 'why.reached': '{label} {value} இப்போதுதான் இலக்கை ({target}) அடைந்தது.',
  'why.worse': '{label} {value} இலக்கை ({target}) விட அதிகம், மோசமடைகிறது.', 'why.improving': '{label} {value} இலக்கை ({target}) விட அதிகம், ஆனால் முன்னேற்றம் உள்ளது.', 'why.above': '{label} {value} இலக்கை ({target}) விட அதிகம்.',
  'why.untreated': ' தற்போதைய மருந்துப் பட்டியலில் சிகிச்சை இல்லை.',
  'care.start': 'தொடங்கப்பட்டது', 'care.stop': 'நிறுத்தப்பட்டது', 'care.increase': 'அதிகரிக்கப்பட்டது', 'care.decrease': 'குறைக்கப்பட்டது', 'care.change': 'மாற்றப்பட்டது',
  'care.med': '{drug}{dose} — {verb}', 'care.hypo': 'இரத்தச் சர்க்கரைக் குறைவு {g} mg/dL{sev}',
  'care.reached': '{label} இலக்கை அடைந்தது', 'care.left': '{label} இலக்கிலிருந்து விலகியது', 'care.first': 'முதல் பதிவான {label}', 'care.attention': '{label} — கவனம் தேவை',
  'care.detail': '{value} (இலக்கு {target}).',
  'scr.retinal': 'விழித்திரை பரிசோதனை', 'scr.foot': 'பாத பரிசோதனை', 'scr.liverUltrasound': 'கல்லீரல் அல்ட்ராசவுண்ட்', 'scr.homeBloodPressure': 'வீட்டு இரத்த அழுத்தப் பதிவு',
  'cond.t2dm': 'வகை 2 நீரிழிவு', 'cond.dyslipidemia': 'கொழுப்பு சீர்குலைவு', 'cond.hypertension': 'உயர் இரத்த அழுத்தம்', 'cond.dkd': 'நீரிழிவு சிறுநீரக நோய்',
  'cond.obesity': 'உடல் பருமன்', 'cond.steatosis': 'கொழுப்பு கல்லீரல்', 'cond.retinopathy': 'நீரிழிவு விழித்திரை நோய்',
  'm.fastingGlucose': 'உண்ணாநிலை குளுக்கோஸ்', 'm.eag': 'மதிப்பிடப்பட்ட சராசரி குளுக்கோஸ்', 'm.ldl': 'LDL கொழுப்பு', 'm.tg': 'ட்ரைகிளிசரைடுகள்',
  'm.hdl': 'HDL கொழுப்பு', 'm.nonHdl': 'HDL அல்லாத கொழுப்பு', 'm.totalCholesterol': 'மொத்த கொழுப்பு', 'm.systolic': 'சிஸ்டாலிக் இரத்த அழுத்தம்', 'm.diastolic': 'டயஸ்டாலிக் இரத்த அழுத்தம்',
  'm.heartRate': 'இதயத் துடிப்பு', 'm.uacr': 'சிறுநீர் அல்புமின்/கிரியாட்டினின்', 'm.creatinine': 'கிரியாட்டினின்', 'm.potassium': 'பொட்டாசியம்',
  'm.weight': 'எடை', 'm.waist': 'இடுப்பு சுற்றளவு',
  'part.eyes': 'கண்கள்', 'part.brain': 'மூளை', 'part.heart': 'இதயம்', 'part.septum': 'இதயத் தடுப்புச் சுவர்', 'part.arteries': 'தமனிகள்', 'part.lungs': 'நுரையீரல்', 'part.thyroid': 'தைராய்டு',
  'part.liver': 'கல்லீரல்', 'part.spleen': 'மண்ணீரல்', 'part.pancreas': 'கணையம்', 'part.kidney': 'சிறுநீரகம்', 'part.bladder': 'சிறுநீர்ப்பை', 'part.spine': 'முதுகெலும்பு',
  'part.psoas': 'சோயாஸ் தசை', 'part.paraspinal': 'முதுகெலும்பு அருகுத் தசைகள்', 'part.trunk': 'உடல் அமைப்பு',
  'layer.skin': 'தோல்', 'layer.muscle': 'தசைகள்', 'layer.vascular': 'இரத்த நாளங்கள்', 'layer.organs': 'உறுப்புகள்', 'layer.skeletal': 'எலும்புக்கூடு', 'layer.nerves': 'நரம்புகள்',
  'gate.title': 'உங்கள் குறியீட்டை உள்ளிடவும்',
  'gate.sub': 'இது ஒரு டெமோ — கணக்கு அல்லது கடவுச்சொல் தேவையில்லை. உங்களுக்கு வழங்கப்பட்ட குறியீட்டை உள்ளிடவும்: 6 எழுத்து சோதனைக் குறியீடு ஒரு மாதிரி குடும்பப் பதிவைத் திறக்கும்; மருத்துவமனையின் அணுகல் குறியீடு ஒரு நோயாளியின் முழு வரலாற்றைத் திறக்கும்.',
  'gate.label': 'உங்கள் டெமோ குறியீடு', 'gate.enter': 'டெமோவைத் திற', 'gate.opening': 'திறக்கிறது…',
  'gate.note': 'மாதிரித் தரவு மட்டுமே — உண்மையான நோயாளிகள் இல்லை. நீங்கள் சேர்ப்பது அல்லது திருத்துவது உங்கள் உலாவியில் மட்டுமே இருக்கும்; மீண்டும் ஏற்றும்போது மீட்டமைக்கப்படும்.',
  'gate.back': '← உள்நுழைவுக்குத் திரும்பு', 'gate.badCode': 'இந்தக் குறியீடு செல்லாது. டெமோ இணைப்பைப் பகிர்ந்தவரிடம் சரிபார்க்கவும்.',
  'gate.short': '6 எழுத்து சோதனைக் குறியீட்டையோ உங்களுக்கு வழங்கப்பட்ட அணுகல் குறியீட்டையோ உள்ளிடவும்.',
  // settings · names · header menu
  's.title': "அமைப்புகள்",
  's.account': "கணக்கு",
  's.appearance': "தோற்றம்",
  's.access': "அணுகல் & வரலாறு",
  's.data': "தரவு மூலங்கள்",
  's.agreements': "ஒப்பந்தங்கள்",
  's.profile': "சுயவிவரம்",
  's.yourRecord': "உங்கள் பதிவு",
  's.editProfile': "சுயவிவரத்தைத் திருத்து",
  's.logout': "வெளியேறு",
  's.country': "நாடு / பகுதி",
  's.change': "மாற்று",
  's.dob': "பிறந்த தேதி",
  's.age': " · வயது {n}",
  's.minor': " · சிறார்",
  's.notSet': "அமைக்கப்படவில்லை",
  's.sex': "பாலினம்",
  's.patientEmail': "நோயாளி மின்னஞ்சல்",
  's.restrictions': "கட்டுப்பாடுகள்",
  's.eid': "empowered ID",
  's.coOwners': "இணை உரிமையாளர்கள் — நோயாளி வழங்கிய சம அணுகல்",
  's.managedBy': "நிர்வகிப்பவர்",
  's.googleAccount': "Google கணக்கு",
  's.you': " · நீங்கள்",
  's.managingParent': "நிர்வகிக்கும் பெற்றோர்",
  's.guardiansWith': "அணுகல் உள்ள பெற்றோர் / பாதுகாவலர்கள்",
  's.removeGuardian': "பாதுகாவலரை நீக்கு",
  's.revoke': "அணுகலை நீக்கு",
  's.signin': "உள்நுழைவு & பாதுகாப்பு",
  's.google': "Google உள்நுழைவு",
  's.googleSub': "நீங்கள் Google மூலம் பாதுகாப்பாக உள்நுழைகிறீர்கள் — தனி கடவுச்சொல் தேவையில்லை.",
  's.twofa': "இரு-படி அங்கீகாரம் (2FA)",
  's.authApp': "அங்கீகரிப்புச் செயலி",
  's.soon': "விரைவில்",
  's.activity': "செயல்பாடு & நேர்மை",
  's.signedKey': "உங்கள் விசையுடன் உள்நுழைந்துள்ளது:",
  's.verified': "✓ சரிபார்க்கப்பட்டது",
  's.tampered': "⚠ மாற்றப்பட்டுள்ளது",
  's.noActivity': "இன்னும் செயல்பாடு இல்லை.",
  's.typeface': "எழுத்துரு",
  's.textSize': "எழுத்து அளவு",
  's.preview': "முன்னோட்டம்",
  's.theme': "தீம்",
  's.dark': "இருண்ட",
  's.light': "வெளிர்",
  's.system': "கணினி",
  's.language': "மொழி",
  's.scale.0': "இயல்பு",
  's.scale.1': "பெரியது",
  's.scale.2': "மிகப் பெரியது",
  's.scale.3': "மிக மிகப் பெரியது",
  's.careTeam': "பராமரிப்புக் குழு — அணுகல்",
  's.guardiansFor': "{name} அவர்களின் பதிவைப் பார்க்கவும் சேர்க்கவும் முடிந்த பாதுகாவலர்கள்:",
  's.addGuardian': "பாதுகாவலரைச் சேர்",
  's.guardianNote': "அவர்கள் இந்த Google மின்னஞ்சலுடன் உள்நுழைவார்கள்; சுயவிவரம் அவர்களின் கணக்கில் தோன்றும்.",
  's.sharedWithYou': "இந்தச் சுயவிவரம் அதன் உரிமையாளரால் உங்களுடன் பகிரப்பட்டுள்ளது.",
  's.providers': "சேவை வழங்குநர்கள் — அணுகல்",
  's.providersSub': "ஒரு மருத்துவருக்கு அவரது Google கணக்கின் மூலம் ஒரு குழந்தையின் பதிவுக்கான அணுகலை வழங்கவும். பகிர்வு சுயவிவர வாரியானது — நீங்கள் தேர்ந்தெடுக்கும் குழந்தையை மட்டுமே வழங்குநர் பார்ப்பார், உங்கள் மற்ற குழந்தைகளை அல்ல.",
  's.sharingFor': "யாருடைய பதிவைப் பகிர்கிறீர்கள்",
  's.unnamed': "பெயரிடப்படாதது",
  's.thisPatient': "இந்த நோயாளி",
  's.noProviders': "{name} அவர்களின் பதிவில் இன்னும் சேவை வழங்குநர்கள் இல்லை.",
  's.addTo': "{name} பதிவில் சேர்",
  's.providerNote': "வழங்குநர் இந்த Google மின்னஞ்சலுடன் உள்நுழைவார்; அவரது கணக்கில் {name} அவர்களின் பதிவு மட்டுமே தோன்றும்.",
  's.accessTitle': "கணக்கு அணுகல் — கடந்த 30 நாட்கள்",
  's.whoOpened': "{name} அவர்களின் பதிவை யார் திறந்தார்கள், புதியது முதலில்.",
  's.viewsPeople': " {v} பார்வைகள் · {p} நபர்கள்.",
  's.loading': "ஏற்றுகிறது…",
  's.noAccess': "கடந்த 30 நாட்களில் அணுகல் பதிவு இல்லை.",
  's.youCap': "நீங்கள்",
  's.someone': "யாரோ",
  's.youTag': "நீங்கள்",
  's.sharedAccess': "பகிர்ந்த அணுகல்",
  's.viewsLast': "{n} பார்வைகள் · கடைசியாக {when}",
  's.accessNote': "மீண்டும் வருகைகள் ஒவ்வொரு நபருக்கும் தொகுக்கப்படும். ஒவ்வொரு உள்நுழைவுக்கும் ஒருமுறை அணுகல் பதிவாகும்; 30 நாட்களுக்கு முந்தையவை காட்டப்படாது.",
  's.accountAccess': "கணக்கு அணுகல்",
  's.ownerOnly': "அணுகல் வரலாறு பதிவு உரிமையாளருக்கு மட்டுமே கிடைக்கும்.",
  's.dataSub': "empowered உதவியாளருடன் உரையாடி தரவைச் சேர்க்கவும், அல்லது பதிவுகளைப் பதிவேற்றவும் (C-CDA, PDF, DICOM, CSV, Excel) — ஒவ்வொன்றும் உங்கள் மறையாக்கப்பட்ட கணக்கில் மாற்ற முடியாத பதிவாகச் சேமிக்கப்படும்.",
  's.uploads': "பதிவேற்றங்கள் & இறக்குமதிகள்",
  's.connected': "இணைக்கப்பட்ட மூலங்கள்",
  's.epicSub': "பதிவுகளைத் தானாக ஒத்திசைக்க உங்கள் மருத்துவமனையின் Epic கணக்கை இணைக்கவும்.",
  's.agrSub': "நீங்கள் ஏற்றுக்கொண்ட உள்நுழைவு ஒப்பந்தங்கள் — முழு உரையையும் உங்கள் கையொப்பத்தையும் படிக்க ஒன்றைத் திறக்கவும்:",
  's.version': "பதிப்பு {v}",
  's.accepted': "ஏற்றுக்கொள்ளப்பட்டது {d}",
  's.viewSig': "பார்வை & கையொப்பம்",
  's.noAgr': "இன்னும் ஒப்பந்த ஏற்பு பதிவு இல்லை.",
  's.pvTitle': "எழுத்து அளவு முன்னோட்டம்",
  's.pvBody': "வெப்பநிலை 98.6 °F · இதயத் துடிப்பு 88 bpm · WBC 9.6 kU/μL. தேர்ந்தெடுத்த அளவில் செயலியில் உரை இப்படித் தோன்றும்.",
  's.pvLabel': "லேபிள்",
  's.close': "மூடு",
  's.signed': "டிஜிட்டல் முறையில் கையொப்பமிடப்பட்டது",
  's.effective': "நடைமுறை {d}",
  's.legalNote': "ஒப்பந்தம், அது கையொப்பமிடப்பட்ட மொழியான ஆங்கிலத்தில் காட்டப்படுகிறது.",
  'rel.self': "சுயம்",
  'rel.spouse': "வாழ்க்கைத் துணை",
  'rel.parent': "பெற்றோர்",
  'rel.child': "குழந்தை",
  'rel.dependent': "சார்ந்தவர்",
  'rel.other': "மற்றவர்",
  'sex.Male': "ஆண்",
  'sex.Female': "பெண்",
  'hdr.profilesUnder': "உங்கள் கணக்கின் சுயவிவரங்கள்",
  'hdr.shared': " · பகிரப்பட்டது",
  'hdr.addProfile': "+ சுயவிவரத்தைச் சேர்",
  'hdr.default': "உள்நுழைவில் இயல்பாக அமை",
  'hdr.defaultOn': "உள்நுழைவில் இயல்பு (நீக்கக் கிளிக் செய்க)",
  'hdr.assistant': "empowered உதவியாளர்",
  'hdr.account': "கணக்கு & சுயவிவரங்கள்",
  'hdr.yourAccount': "உங்கள் கணக்கு",
};

export const DICTS: Record<Lang, Dict> = { en, hi, ta };

// ── names ─────────────────────────────────────────────────────────────────────
// People's names are shown in the chosen script. Known names are transliterated
// from this table (the demo record's patient and the family demo); any other name
// is shown as written, never machine-transliterated (a wrong spelling of someone's
// name is worse than the original script).
const NAMES: Record<string, { hi: string; ta: string }> = {
  Arjun: { hi: 'अर्जुन', ta: 'அர்ஜுன்' }, Nair: { hi: 'नायर', ta: 'நாயர்' },
  Alex: { hi: 'एलेक्स', ta: 'அலெக்ஸ்' }, Ellis: { hi: 'एलिस', ta: 'எல்லிஸ்' }, Jordan: { hi: 'जॉर्डन', ta: 'ஜோர்டான்' },
  Sam: { hi: 'सैम', ta: 'சாம்' }, Rowan: { hi: 'रोवन', ta: 'ரோவன்' },
};
export function localName(l: Lang, name: string | null | undefined): string {
  const s = (name ?? '').trim();
  if (l === 'en' || !s) return s;
  return s.split(/(\s+)/).map((w) => NAMES[w]?.[l] ?? w).join('');
}
/** Initials in the chosen script (first character of the first two name parts). */
export function localInitials(l: Lang, first: string | null | undefined, last: string | null | undefined): string {
  const a = [...localName(l, first)][0] ?? '', b = [...localName(l, last)][0] ?? '';
  return (a + b).toUpperCase() || '·';
}
export const LOCALE: Record<Lang, string> = { en: 'en-US', hi: 'hi-IN', ta: 'ta-IN' };
/** A dictionary key's translation, or the raw value when there is none (e.g. relationship, sex). */
export function maybe(l: Lang, prefix: string, value: string | null | undefined): string {
  const v = value ?? ''; const k = prefix + v;
  return v && (k in DICTS[l] || k in en) ? translate(l, k) : v;
}

export function translate(l: Lang, key: string, params: Record<string, string | number> = {}): string {
  const s = DICTS[l][key] ?? en[key] ?? key;
  return s.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? String(params[k]) : ''));
}
/** Reactive translator: `$t('key', {param})`. */
export const t = derived(lang, (l) => (key: string, params?: Record<string, string | number>) => translate(l, key, params));
export const tNow = (key: string, params?: Record<string, string | number>) => translate(get(lang), key, params);

// ── condition-tracking helpers ────────────────────────────────────────────────
export const measureLabel = (l: Lang, m: MeasureTrack | { spec: { key: string; label: string } }) => translate(l, `m.${m.spec.key}`) === `m.${m.spec.key}` ? m.spec.label : translate(l, `m.${m.spec.key}`);

/** The status reason, built from the track's data in the chosen language. */
export function statusReason(l: Lang, track: ConditionTrack): string {
  const p = track.primary;
  if (!p || !p.latest) return translate(l, 'why.monitoring');
  const s = p.spec;
  const base = { label: measureLabel(l, p), value: fmtValue(p.latest.value, s), target: s.target?.label ?? '', thr: s.attention?.label ?? '' };
  const rx = track.untreated ? translate(l, 'why.untreated') : '';
  if (p.attention) return translate(l, 'why.attention', base) + rx;
  if (p.atGoal == null) return translate(l, 'why.noTarget', base);
  if (p.atGoal) return p.consecutiveAtGoal >= 2 ? translate(l, 'why.controlled', { ...base, n: p.consecutiveAtGoal }) : translate(l, 'why.reached', base);
  if (p.trend === 'worsening') return translate(l, 'why.worse', base) + rx;
  if (p.trend === 'improving') return translate(l, 'why.improving', base);
  return translate(l, 'why.above', base) + rx;
}

/** A care-history entry's title/detail in the chosen language (falls back to the engine's English). */
export function careText(l: Lang, e: CareEvent): { title: string; detail: string } {
  if (!e.key) return { title: e.title, detail: e.detail };
  const p = e.params ?? {};
  const label = p.measure ? translate(l, `m.${p.measure}`) === `m.${p.measure}` ? String(p.label ?? '') : translate(l, `m.${p.measure}`) : String(p.label ?? '');
  switch (e.key) {
    case 'med': return { title: translate(l, 'care.med', { verb: translate(l, `care.${p.action}`), drug: String(p.drug), dose: p.dose ? ` ${p.dose}` : '' }), detail: e.detail };
    case 'hypo': return { title: translate(l, 'care.hypo', { g: String(p.glucose), sev: p.severity ? ` (${p.severity})` : '' }), detail: e.detail };
    case 'screen': return { title: translate(l, `scr.${p.stream}`), detail: e.detail };
    case 'reached': case 'left': case 'first':
      return { title: translate(l, `care.${e.key}`, { label }), detail: p.value ? translate(l, 'care.detail', { value: String(p.value), target: String(p.target ?? '') }) : e.detail };
    case 'attention': return { title: translate(l, 'care.attention', { label }), detail: e.detail };
    default: return { title: e.title, detail: e.detail };
  }
}
